import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { DisbursementMethod } from 'types';
import { Tooltip } from '../Tooltip';

interface ITagProps {
  appearance?: Appearance | DisbursementMethod;
  children?: ReactNode;
  className?: string;
  dataCy?: string;
  label: string;
  onClick?: () => void;
  size?: Size.SMALL;
  tooltipId?: string;
  tooltipMessage?: string | ReactNode;
  tooltipPlace?: 'top' | 'right' | 'bottom' | 'left';
}

const Layout = styled.span<Partial<ITagProps>>`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: fit-content;
  height: ${toRem(24.75)};
  padding: 0 ${toRem(8.75)};
  border-radius: ${toRem(5.5)};
  background-color: #ecedef;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  text-transform: capitalize;
  color: ${theme.color.neutral[900]};
  cursor: default;

  ${({ size }) =>
    size === Size.SMALL &&
    css`
      height: ${toRem(20)};
      padding: 0 ${toRem(5.5)};
      font-size: ${toRem(11)};
    `}

  ${({ tooltipMessage }) =>
    tooltipMessage &&
    css`
      cursor: pointer;
    `}

    ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}

    ${({ appearance }) =>
    appearance === Appearance.PRIMARY &&
    css`
      background-color: ${theme.color.primary[25]};
    `}

    ${({ appearance }) =>
    appearance === Appearance.WARNING &&
    css`
      background-color: ${theme.color.warning.light};
    `}

    ${({ appearance }) =>
    appearance === Appearance.DANGER &&
    css`
      background-color: ${theme.color.danger.light};
    `}

    ${({ appearance }) =>
    appearance === Appearance.NEUTRAL &&
    css`
      background-color: ${theme.color.neutral[100]};
    `}

    ${({ appearance }) =>
    appearance === Appearance.SUCCESS &&
    css`
      background-color: ${theme.color.success.light};
    `}

    ${({ appearance }) =>
    appearance === DisbursementMethod.INSTANT &&
    css`
      background-color: ${theme.color.secondary[25]};
      font-size: ${toRem(10)};
      padding: ${toRem(4)} ${toRem(8)};
      border-radius: ${toRem(4)};
    `}

    ${({ appearance }) =>
    appearance === DisbursementMethod.PAYROLL &&
    css`
      background-color: ${theme.color.secondary[25]};
      font-size: ${toRem(10)};
      padding: ${toRem(4)} ${toRem(8)};
      border-radius: ${toRem(4)};
    `}
`;

const Children = styled.span<Partial<ITagProps>>``;

const Tag = ({ label, appearance = Appearance.NEUTRAL, className, children, onClick, size = undefined, tooltipId, tooltipMessage, tooltipPlace = 'right', dataCy = 'tag', ...rest }: ITagProps) => {
  const content = (
    <>
      {label}
      {children && <Children>{children}</Children>}
    </>
  );

  const getTooltipId = tooltipId || label;

  return tooltipMessage ? (
    <>
      <Layout className={className} appearance={appearance} size={size} onClick={onClick} data-cy={dataCy} data-tooltip-id={getTooltipId} {...rest}>
        {content}
        <Tooltip id={getTooltipId} place={tooltipPlace}>
          {tooltipMessage}
        </Tooltip>
      </Layout>
    </>
  ) : (
    <Layout className={className} appearance={appearance} size={size} onClick={onClick} data-cy={dataCy} {...rest}>
      {content}
    </Layout>
  );
};

export default Tag;
